import {
  ALL_TEAMS_OPTION,
  SELECTED_TEAM_ID_KEY,
  SELECTED_TEAM_NAME_KEY,
} from '@dc-shared-web/lib/constants'
import { userHasExplicitPermission } from '@/app/helpers/userHelper'
import { User } from '@/dc-shared-web/src/hooks/api/user/types'
import { DOCUMENT_CRUNCH_HQ, useAuth } from '@/dc-shared-web/src'
import { useSearchParams } from 'next/navigation'
import { useIsProjectEditor } from '@/dc-shared-web/src/hooks/api/user/useIsProjectEditor'

const getIsTeamEditor = (user: User, teamUUID: string | null): boolean => {
  if (!teamUUID) {
    return false
  }
  return userHasExplicitPermission(user, {
    resource_uuid: teamUUID,
    role: 'EDITOR',
  })
}

export const useDashboardPermissions = (
  currentPage: 'dashboard' | 'folders' | 'projects'
) => {
  const { user } = useAuth()
  const params = useSearchParams()

  const selectedTeamId = params.get(SELECTED_TEAM_ID_KEY)
  const selectedTeamName = params.get(SELECTED_TEAM_NAME_KEY)

  const selectedTeamIdWithDefault = selectedTeamId || ALL_TEAMS_OPTION.text
  const hasTeamSelected = !!selectedTeamId
  const isSuperAdmin = user.is_super_admin
  const isSuperAdminAndNotAllTeamsSelected =
    isSuperAdmin && selectedTeamIdWithDefault !== ALL_TEAMS_OPTION.text

  const isUserInHQTeam = selectedTeamName === DOCUMENT_CRUNCH_HQ
  const isSuperAdminAndInHQTeam = isSuperAdmin && isUserInHQTeam
  const isTeamEditor = getIsTeamEditor(user, selectedTeamId)
  const isUnaffiliatedUser = user._isUnaffiliatedUser
  const isProjectEditor = useIsProjectEditor()
  // If user is not super admin or is super admin and in HQ team, show actions
  const shouldShowActions = !isSuperAdmin || isSuperAdminAndInHQTeam

  /* User can view crunches if:
    - They are not a project editor
    OR
    - They are super admin
    OR
    - They are not a super admin and a team is selected
  */
  const canViewCrunches =
    !isProjectEditor && (isSuperAdmin || (!isSuperAdmin && hasTeamSelected))

  /*
    User can see explorers if:
    - They have selected a team
    OR
    - They are a super admin and have not selected the all teams option
  */
  const canViewExplorers = hasTeamSelected || isSuperAdminAndNotAllTeamsSelected
  const canViewFoldersOnDashboard =
    !isUnaffiliatedUser && !isProjectEditor && currentPage === 'dashboard'

  const isTeamEditorOnDashboard = isTeamEditor && currentPage === 'dashboard'
  // If user is not unaffiliated or team editor on dashboard, show the create actions menu
  const shouldShowCreateActionsMenu =
    !isUnaffiliatedUser && !isTeamEditorOnDashboard

  const canAddFolder =
    shouldShowCreateActionsMenu && (!isSuperAdmin || isSuperAdminAndInHQTeam)

  const canAddProject =
    shouldShowCreateActionsMenu &&
    currentPage !== 'projects' &&
    (!isSuperAdmin || isSuperAdminAndInHQTeam)
  return {
    canViewExplorers,
    canViewFoldersOnDashboard,
    shouldShowActions,
    shouldShowCreateActionsMenu,
    canAddFolder,
    canAddProject,
    canViewCrunches,
  }
}
